import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Star, Instagram, ArrowRight, VolumeX, BookOpen, Timer, ChevronDown } from 'lucide-react';
import { PRODUCTS } from '../data/products';
import { useCartStore } from '../store/useCartStore';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 24
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: [0.23, 1, 0.32, 1]
}} className={className} data-id="element-105" data-mp="2b2dqvnoy5sro">
    {children}
  </motion.div>;
const FaqItem = ({
  faq,
  delay
}: {
  faq: {
    q: string;
    a: string;
  };
  delay: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <FadeIn delay={delay} data-id="element-106" data-mp="lnz4ha1eihzc">
      <div className="border-b border-brand-primary/10" data-id="element-107" data-mp="2zjgq4wccm2d0">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-7 flex items-center justify-between text-left focus:outline-none group" data-id="element-108" data-mp="2zgu822ru04gj">
          <span className="font-serif font-normal text-brand-primary text-xl lg:text-2xl pr-8 group-hover:opacity-70 transition-opacity duration-200" data-id="element-109" data-mp="38itrt2u8dmn6">
            {faq.q}
          </span>
          <ChevronDown className={`w-4 h-4 text-brand-primary flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} data-id="element-110" data-mp="3i8dbubtojmjm" />
        </button>
        <AnimatePresence data-id="element-111" data-mp="3rbechwzs0lfw">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-112" data-mp="3jsu3ln0xjm">
              <p className="pb-8 text-brand-text text-base lg:text-lg leading-relaxed font-light max-w-2xl" data-id="element-113" data-mp="355ss0l1dllbn">
                {faq.a}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </FadeIn>;
};
export function LandingPage() {
  const navigate = useNavigate();
  const {
    addItem
  } = useCartStore();
  const scrollToProducts = () => {
    const element = document.getElementById('products');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleBuyClick = (e: React.MouseEvent, product: (typeof PRODUCTS)[0]) => {
    e.stopPropagation();
    addItem(product);
  };
  const problems = [{
    icon: <VolumeX className="w-5 h-5 text-brand-accent" strokeWidth={1.5} data-id="element-114" data-mp="3sver78hr00jn" />,
    title: 'Нескінченні нагадування',
    desc: 'Втомилися щодня повторювати «почисть зуби», «прибери іграшки» або «заправ ліжко»?'
  }, {
    icon: <BookOpen className="w-5 h-5 text-brand-accent" strokeWidth={1.5} data-id="element-115" data-mp="5sidyp6x4kgb" />,
    title: 'Сварки через уроки',
    desc: 'Дитина сідає за домашнє завдання тільки після довгих вмовлянь та зіпсованого настрою?'
  }, {
    icon: <Timer className="w-5 h-5 text-brand-accent" strokeWidth={1.5} data-id="element-116" data-mp="u9hesy83b69k" />,
    title: 'Стресові ранки',
    desc: 'Ранкові збори в садок або школу перетворюються на хаос і забирають енергію на весь день?'
  }];
  const howItWorks = [{
    title: 'Формує корисні звички',
    desc: 'Вмивання, прибирання, читання стають автоматичними діями.'
  }, {
    title: 'Мотивує через гру та нагороди',
    desc: 'Збери 7 зірочок за тиждень — отримай заслужений приз!'
  }, {
    title: 'Зменшує стрес і нагадування',
    desc: 'Більше жодних нагадувань. Дитина сама бачить свій план.'
  }, {
    title: 'Зона Workout',
    desc: 'Фізична активність щодня: присідання, віджимання, планка.'
  }];
  const faqs = [{
    q: 'Для якого віку підходить планер?',
    a: 'Планер ідеально підходить для дітей від 4 до 10 років. Малюки можуть орієнтуватися по картинках, а старші діти — читати завдання самостійно.'
  }, {
    q: 'Як він кріпиться?',
    a: 'Планер має повністю магнітну основу. Він надійно тримається на холодильнику або будь-якій іншій металевій поверхні і не сповзає.'
  }, {
    q: 'Чим писати на планері?',
    a: 'У комплекті йде спеціальний маркер з губкою. Дитина може легко ставити галочки та стирати їх наприкінці тижня. Поверхня планера багаторазова.'
  }];
  const reviews = [{
    name: 'Анна',
    role: 'мама 6-річного Максима',
    text: 'Це просто порятунок! Раніше кожен ранок починався зі сліз та вмовлянь почистити зуби. Тепер син сам біжить до холодильника відмічати галочки.'
  }, {
    name: 'Олена',
    role: 'мама двійнят',
    text: "Купили відразу два планери. У дітей з'явився змагальний дух! Самі заправляють ліжко, роблять зарядку. Я нарешті можу спокійно випити каву вранці."
  }, {
    name: 'Ігор',
    role: 'тато 8-річної Софії',
    text: "Відмінна річ для привчання до дисципліни без тиску. Донька сама обирає собі 'Завдання тижня' і дуже пишається, коли збирає всі зірочки."
  }];
  return <div className="min-h-screen bg-brand-bg text-brand-text overflow-x-hidden selection:bg-brand-accent/20 selection:text-brand-primary pt-24" data-id="element-117" data-mp="ylx1zkcjjzxw">
      {/* Hero */}
      <section className="relative pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden" data-id="element-118" data-mp="6upgym1npw3k">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative" data-id="element-119" data-mp="avj7ln6hhp05">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-120" data-mp="aw2nkyckq3bj">
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.7,
            ease: [0.23, 1, 0.32, 1]
          }} className="text-center lg:text-left order-1" data-id="element-121" data-mp="fu453eucpukw">
              <div className="flex items-center gap-3 justify-center lg:justify-start mb-8" data-id="element-122" data-mp="1w8mamd1r1asq">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-accent" data-id="element-123" data-mp="16kqbcbeusn7g" />
                <span className="text-xs font-medium text-brand-muted tracking-[0.2em] uppercase" data-id="element-124" data-mp="16mtc9qhjo0qy">Преміальна якість</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-normal leading-[1.05] mb-8 text-brand-primary tracking-tight" data-id="element-125" data-mp="1gjfv69qb8hqc">
                Це не просто <br data-id="element-126" data-mp="1bi8n09k99gj5" />
                <span className="text-brand-accent italic" data-id="element-127" data-mp="nu8vv8j17o41">
                  Планер.
                </span>
              </h1>

              <div className="hidden lg:block mb-12" data-id="element-128" data-mp="iwgs9h90ydk0">
                <p className="text-lg text-brand-text leading-relaxed max-w-md font-light" data-id="element-129" data-mp="xkbnb4ai8u09">
                  Елегантний спосіб навчити дитину самостійності. Без криків, без нагадувань, з любов'ю до деталей.
                </p>
              </div>

              <div className="hidden lg:flex flex-col sm:flex-row gap-6 justify-center lg:justify-start" data-id="element-130" data-mp="52zd08ndfmzs">
                <button onClick={scrollToProducts} className="px-10 py-4 bg-brand-primary hover:bg-brand-primaryHover text-white text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 flex items-center justify-center gap-3 group" data-id="element-131" data-mp="44u76pelyfo">
                  Обрати дизайн
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" data-id="element-132" data-mp="21s93syt6uvjy" />
                </button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.15,
            ease: [0.23, 1, 0.32, 1]
          }} className="relative order-2 max-w-md mx-auto lg:max-w-full flex flex-col items-center" data-id="element-133" data-mp="2ugoe581dkpoy">
              <div className="relative overflow-hidden bg-brand-surface mb-10 w-full aspect-[4/3] border border-brand-primary/10" data-id="element-134" data-mp="1a2xjji9vtzrw">
                <img src="https://cdn.magicpatterns.com/uploads/cdrFBjpV29uCPC4QsJUG8h/IMG_5206.png" alt="MyPlaner - Інтерактивний планер для дітей" className="w-full h-full object-cover" data-id="element-135" data-mp="1e4red9xec9k5" />
              </div>

              <div className="lg:hidden mb-10 text-center w-full" data-id="element-136" data-mp="1wqw1shlmna95">
                <p className="text-base text-brand-text leading-relaxed font-light" data-id="element-137" data-mp="1wtibams2vzu0">
                  Елегантний спосіб навчити дитину самостійності. Без криків, без нагадувань, з любов'ю до деталей.
                </p>
              </div>

              <div className="flex lg:hidden flex-col sm:flex-row gap-4 w-full justify-center" data-id="element-138" data-mp="2kgqd2uqasqra">
                <button onClick={scrollToProducts} className="w-full px-8 py-4 bg-brand-primary hover:bg-brand-primaryHover text-white text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 flex items-center justify-center gap-3" data-id="element-139" data-mp="2fpqk20kausu2">
                  Обрати дизайн
                  <ArrowRight className="w-4 h-4" data-id="element-140" data-mp="33ejgrz8qx4pq" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problems */}
      <section className="py-24 lg:py-32 border-t border-brand-primary/10" data-id="element-141" data-mp="3fqvdvjzclin2">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-142" data-mp="3kox36mndae6z">
          <FadeIn data-id="element-143" data-mp="3krjacl6ciroo">
            <div className="text-center mb-16 lg:mb-20" data-id="element-144" data-mp="3oqig27zwish6">
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-brand-primary mb-6 tracking-tight" data-id="element-145" data-mp="3os3alxm7ubf1">
                Знайомі ситуації?
              </h2>
              <p className="text-lg text-brand-muted max-w-2xl mx-auto font-light" data-id="element-146" data-mp="2iih20ey2xxi">
                Кожен день схожий на день бабака, а прості справи перетворюються
                на випробування.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-brand-primary/10 border-t border-b border-brand-primary/10" data-id="element-147" data-mp="cb6bioigcpt2">
            {problems.map((item, idx) => <FadeIn key={idx} delay={idx * 0.1} className="py-10 md:py-0" data-id="element-148" data-mp="l7p2hv74i3n0" data-mpi={idx}>
                <div className="h-full flex flex-col md:px-10 md:py-12" data-id="element-149" data-mp="1g91hqjgyz6z8">
                  <div className="mb-6" data-id="element-150" data-mp="1l7cr6t3crfgx">{item.icon}</div>
                  <h3 className="text-xl font-serif font-normal mb-3 text-brand-primary" data-id="element-151" data-mp="1q5ebtigffmui">
                    {item.title}
                  </h3>
                  <p className="text-brand-text text-base leading-relaxed font-light" data-id="element-152" data-mp="1uwwc04amljef">
                    {item.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* How it works + Buy */}
      <section className="py-24 lg:py-32 bg-brand-surfaceAlt" data-id="element-153" data-mp="28vcjvyk85fy5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-154" data-mp="28u1ejub4s04x">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-155" data-mp="2dtno9trr2y8z">
            <div className="relative overflow-hidden bg-brand-surface aspect-[4/5] lg:aspect-auto lg:h-[640px] order-2 lg:order-1 border border-brand-primary/10" data-id="element-156" data-mp="eimjcxzcp8hs">
              <img src="https://cdn.magicpatterns.com/uploads/w3mah8GUReZcNXMHpqYgWg/IMG_5249.jpg" alt="Дівчинка заповнює планер" className="absolute inset-0 w-full h-full object-cover object-center" data-id="element-157" data-mp="2wr7gst81wo6d" />
            </div>

            <div className="flex flex-col order-1 lg:order-2" data-id="element-158" data-mp="2nwt48yewnrqy">
              <FadeIn data-id="element-159" data-mp="2j13ygdcalem7">
                <h2 className="text-4xl md:text-5xl font-serif font-normal text-brand-primary mb-10 tracking-tight" data-id="element-160" data-mp="2j1nh5w9ro71a">
                  Як працює MyPlaner?
                </h2>

                <div className="border-t border-brand-primary/10 mb-12" data-id="element-161" data-mp="3bpt1wj0tlw14">
                  {howItWorks.map((item, idx) => <div key={idx} className="flex gap-6 py-6 border-b border-brand-primary/10" data-id="element-162" data-mp="36rhueexzzxm2" data-mpi={idx}>
                      <span className="text-sm font-medium text-brand-accent tabular-nums pt-1 w-6 flex-shrink-0" data-id="element-163" data-mp="1vb9kttk5fm0w">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <div data-id="element-164" data-mp="1rtly2ddzbfz8">
                        <h4 className="text-lg font-serif font-normal text-brand-primary mb-1.5" data-id="element-165" data-mp="1rq7vb41el4r9">
                          {item.title}
                        </h4>
                        <p className="text-brand-text text-base leading-relaxed font-light" data-id="element-166" data-mp="1iqkv2lkeu4zs">
                          {item.desc}
                        </p>
                      </div>
                    </div>)}
                </div>

                <div className="flex flex-col sm:flex-row items-center sm:items-end justify-between gap-8 pt-2" data-id="element-167" data-mp="1dsj8vrrotpk3">
                  <div className="text-center sm:text-left" data-id="element-168" data-mp="101ei7f6as7lw">
                    <p className="text-xs text-brand-muted font-medium uppercase tracking-[0.2em] mb-2" data-id="element-169" data-mp="zz1q496tsgv4">
                      Інвестиція в спокій
                    </p>
                    <div className="text-4xl font-serif font-normal text-brand-primary" data-id="element-170" data-mp="q49tp8ozc01x">
                      1000 <span className="text-lg font-sans font-light text-brand-muted" data-id="element-171" data-mp="v10axzcit1ox">грн</span>
                    </div>
                  </div>
                  <button onClick={scrollToProducts} className="w-full sm:w-auto px-10 py-4 bg-brand-primary hover:bg-brand-primaryHover text-white text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 flex items-center justify-center gap-3 group" data-id="element-172" data-mp="bi306v76zfgn">
                    Замовити зараз
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" data-id="element-173" data-mp="udjbesm9a0ns" />
                  </button>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-24 lg:py-32 bg-brand-bg" data-id="element-174" data-mp="z5kk64k7t992">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-175" data-mp="1d5lltm8b0kpl">
          <FadeIn data-id="element-176" data-mp="1d653r2xyf93m">
            <div className="text-center mb-16 lg:mb-20" data-id="element-177" data-mp="1d3iy58umj3o1">
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-brand-primary mb-6 tracking-tight" data-id="element-178" data-mp="1951p2jrn7j3z">
                Оберіть свій дизайн
              </h2>
              <p className="text-lg text-brand-muted max-w-2xl mx-auto font-light" data-id="element-179" data-mp="37lkbt34oj8pt">
                Створені з увагою до деталей, щоб ідеально доповнити інтер'єр дитячої кімнати.
              </p>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-primary/10 border border-brand-primary/10" data-id="element-180" data-mp="3halr0r3ehm2q">
            {PRODUCTS.map((product, idx) => <FadeIn key={product.id} delay={idx * 0.08} className="bg-brand-bg" data-id="element-181" data-mp="2omptjk60bq4w" data-mpi={idx}>
                <div onClick={() => navigate(`/product/${product.id}`)} className="group flex flex-col h-full cursor-pointer bg-brand-bg" data-id="element-182" data-mp="2tm2hy9cj159l">
                  <div className="relative aspect-[3/4] overflow-hidden bg-brand-surfaceAlt" data-id="element-183" data-mp="2xlbchgfe5767">
                    <img src={product.img} alt={product.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out" data-id="element-184" data-mp="8y6c3i9wsfs2" />
                  </div>
                  <div className="p-6 lg:p-8 flex flex-col flex-grow" data-id="element-185" data-mp="1tenxkl2rqvxw">
                    <h3 className="text-xl font-serif font-normal text-brand-primary mb-2" data-id="element-186" data-mp="1om4bezq0mshh">
                      {product.title}
                    </h3>
                    <p className="text-brand-text text-sm mb-8 flex-grow line-clamp-2 font-light" data-id="element-187" data-mp="1eoyhzktxipe3">
                      {product.desc}
                    </p>

                    <div className="flex items-center justify-between mt-auto pt-6 border-t border-brand-primary/10" data-id="element-188" data-mp="19obwo9sam84c">
                      <div className="text-xl font-serif font-normal text-brand-primary" data-id="element-189" data-mp="15qe735um1k0f">
                        {product.price} <span className="text-xs font-sans font-light text-brand-muted" data-id="element-190" data-mp="10rjoa3wn7vgb">грн</span>
                      </div>
                      <button onClick={e => handleBuyClick(e, product)} className="text-brand-primary hover:text-brand-accent text-xs uppercase tracking-[0.15em] font-medium transition-colors duration-200" data-id="element-191" data-mp="2v3hppjopnn1n">
                        Купити
                      </button>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-brand-surfaceAlt" data-id="element-192" data-mp="1pwkh27ppwxc">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-193" data-mp="1olkldhe19bi">
          <FadeIn data-id="element-194" data-mp="5qqca6o9zvvi">
            <div className="text-center mb-16" data-id="element-195" data-mp="5r8gbph6jedz">
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-brand-primary mb-6 tracking-tight" data-id="element-196" data-mp="3ny5zc4k02i8z">
                Часті запитання
              </h2>
            </div>
          </FadeIn>

          <div data-id="element-197" data-mp="2chy4kpat8b7v">
            {faqs.map((faq, idx) => <FaqItem key={idx} faq={faq} delay={idx * 0.08} data-id="element-198" data-mp="2ckkc4oxpaqq3" data-mpi={idx} />)}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 lg:py-32 bg-brand-bg" data-id="element-199" data-mp="29x9yj4curcbh">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-200" data-mp="29zw44yg6nhr2">
          <FadeIn data-id="element-201" data-mp="24xx2pu406uwr">
            <div className="flex items-center gap-3 justify-center mb-8" data-id="element-202" data-mp="24yovyya4ogjv">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-accent" data-id="element-203" data-mp="2ixole85gszh3" />
              <span className="text-xs font-medium text-brand-muted tracking-[0.2em] uppercase" data-id="element-204" data-mp="2dz4xw3b83fmm">
                Зроблено в Україні
              </span>
            </div>

            <div className="text-center mb-16 lg:mb-20" data-id="element-205" data-mp="1q7poxroxy02e">
              <h2 className="text-4xl md:text-5xl font-serif font-normal text-brand-primary mb-6 tracking-tight" data-id="element-206" data-mp="1m9rzqov67dz0">
                Батьки рекомендують
              </h2>
              <p className="text-lg text-brand-muted font-light" data-id="element-207" data-mp="1v8n8a7lot05h">
                Сотні сімей вже забули про крики вранці.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-brand-primary/10 border-t border-b border-brand-primary/10" data-id="element-208" data-mp="12khlzgezj33j">
            {reviews.map((review, idx) => <FadeIn key={idx} delay={idx * 0.1} className="py-10 md:py-0" data-id="element-209" data-mp="i10myfvy5ny1" data-mpi={idx}>
                <div className="h-full flex flex-col md:px-10 md:py-2" data-id="element-210" data-mp="m0ja7ib8qb41">
                  <div className="flex gap-1 mb-6" data-id="element-211" data-mp="qyksi10ue6eg">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 text-brand-accent fill-brand-accent" data-id="element-212" data-mp="vz8l2r4pyvas" data-mpi={i} />)}
                  </div>
                  <p className="text-brand-text text-base leading-relaxed mb-8 flex-grow font-light italic" data-id="element-213" data-mp="14xaqrjzeec4e">
                    "{review.text}"
                  </p>
                  <p className="font-serif font-normal text-brand-primary text-base" data-id="element-214" data-mp="1envlgwbyawi1">
                    {review.name}
                    <span className="text-brand-muted font-sans font-light text-sm not-italic" data-id="element-215" data-mp="1jpky2getni88"> — {review.role}</span>
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-primary text-brand-bg pt-24 pb-12" data-id="element-216" data-mp="345j0fz6ap3yg">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-217" data-mp="348ezjqtvpyoh">
          <FadeIn data-id="element-218" data-mp="2z7hgzd4yj2mg">
            <h2 className="text-4xl md:text-5xl font-serif font-normal mb-8 tracking-tight" data-id="element-219" data-mp="2z925al0ohpbv">
              Готові до спокійних ранків?
            </h2>
            <p className="text-lg text-brand-bg/60 mb-12 max-w-xl mx-auto font-light" data-id="element-220" data-mp="3rulgj2la6oqu">
              Замовте MyPlaner сьогодні та почніть формувати корисні звички
              вашої дитини вже завтра.
            </p>

            <button className="px-10 py-4 bg-white hover:bg-brand-bg/90 text-brand-primary text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 mb-20 inline-flex items-center gap-3" data-id="element-221" data-mp="3i48ypi4up9oe">
              Замовити за 1000 грн
              <ArrowRight className="w-4 h-4" data-id="element-222" data-mp="9q0eaxchfp7b" />
            </button>
          </FadeIn>

          <div className="border-t border-white/10 pt-12 flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-223" data-mp="9o72l3n0iesg">
            <div className="text-xl font-serif font-normal tracking-tight" data-id="element-224" data-mp="1vc13rx9kkwgm">
              MyPlaner<span className="text-brand-accent" data-id="element-225" data-mp="1ra65l1mxqz72">.</span>
            </div>

            <div className="flex items-center gap-6" data-id="element-226" data-mp="1rca6rbtg9k3n">
              <a href="https://www.instagram.com/myplaner.ua/p/DXq4XE3jJA8/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-brand-bg/60 hover:text-brand-bg transition-colors duration-200 text-xs uppercase tracking-[0.15em] font-medium" data-id="element-227" data-mp="1mbuxowg7ayrh">
                <Instagram className="w-4 h-4" strokeWidth={1.5} data-id="element-228" data-mp="2a6xnb6mtx690" />
                <span data-id="element-229" data-mp="2595u89ag7e38">@myplaner.ua</span>
              </a>
            </div>
          </div>

          <div className="mt-12 text-brand-bg/40 text-xs flex flex-col md:flex-row justify-center gap-8 font-light" data-id="element-230" data-mp="32qe8532daaiq">
            <a href="#" className="hover:text-brand-bg transition-colors duration-200" data-id="element-231" data-mp="2xx1kow9fc9rk">
              Умови доставки та оплати
            </a>
            <a href="#" className="hover:text-brand-bg transition-colors duration-200" data-id="element-232" data-mp="3a8lwbz0586cc">
              Політика конфіденційності
            </a>
            <span data-id="element-233" data-mp="3f75mkblrs4ai">
              © {new Date().getFullYear()} MyPlaner. Всі права захищені.
            </span>
          </div>
        </div>
      </footer>
    </div>;
}